import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEALER_FUND_STATEMENT_PER_PAGE } from '../services/dealerFundStatement';
import type { DealerFundStatement } from '../types/dealerFundStatement.types';
import { buildAgreementRefMap, useAgreementSummariesByIds } from './useAgreementSummariesByIds';
import { buildDealerNameMap, useDealersByIds } from './useDealersByIds';
import { useDealerFundStatementQuery } from './useDealerFundStatementQuery';

export interface DealerFinancialsView {
    isLoading: boolean;
    isError: boolean;
    unverified: boolean;
    notEntitled: boolean;
    statement: DealerFundStatement | null;
    dealerNames: Map<string, string>;
    agreementRefs: Map<string, string>;
    agreementRefsFailed: boolean;
    onPageChange: (page: number) => void;
    onRetry: () => void;
}

export function useDealerFinancials(dealerId: string): DealerFinancialsView {
    const [page, setPage] = useState(1);
    const query = useDealerFundStatementQuery(dealerId, page, DEALER_FUND_STATEMENT_PER_PAGE, true);
    const result = query.data;
    const statement = result?.status === 'ok' ? result.statement : null;
    const lastPage = statement?.meta.lastPage ?? null;

    useEffect(() => {
        if (lastPage !== null && page > lastPage) setPage(lastPage);
    }, [page, lastPage]);

    const sellerIds = useMemo(
        () => Array.from(new Set(statement?.agreements.map((row) => row.sellingDealerId) ?? [])),
        [statement],
    );
    const names = useDealersByIds(sellerIds, sellerIds.length > 0);
    const dealerNames = useMemo(() => buildDealerNameMap(names.data), [names.data]);
    const agreementIds = useMemo(
        () => Array.from(new Set(statement?.agreements.map((row) => row.agreementId) ?? [])),
        [statement],
    );
    const refs = useAgreementSummariesByIds(agreementIds, agreementIds.length > 0);
    const refsData = refs.isError ? undefined : refs.data;
    const agreementRefs = useMemo(() => buildAgreementRefMap(refsData), [refsData]);
    const { refetch } = query;
    const onRetry = useCallback(() => { void refetch({ cancelRefetch: false }); }, [refetch]);

    return {
        isLoading: query.isLoading,
        isError: query.isError,
        unverified: result?.status === 'unverified',
        notEntitled: result?.status === 'not_entitled',
        statement,
        dealerNames,
        agreementRefs,
        agreementRefsFailed: refs.isError,
        onPageChange: setPage,
        onRetry,
    };
}
